import { CART_LINE_ATTRIBUTE_KEYS } from '@utils/constants/cart';
import { FRAME_COLORS, LENS_COLORS, LENSES_PACKAGES } from '@constants';
import { Bundle, NormalizedCartLine } from '@ts/cart';
import { RxOption } from '@ts/components';
import { NormalizedVariant } from '@ts/product';
import { parseBaseFrameVariant } from './shopify';
import { BASE_FRAME_LENS_OPTIONS, RX_TYPE } from './constants/base-skus';

type BundleContext = {
	bundle: Bundle;
	prescriptionOptions: RxOption[];
	frameColor: string;
	baseColor: string;
	lensColor?: LENS_COLORS[number];
	isV2Subscription?: boolean;
};

type BundleReturn = {
	color: FRAME_COLORS[number];
	tops: Record<string, NormalizedVariant>;
	lenses: BASE_FRAME_LENS_OPTIONS[];
	lensColor: LENS_COLORS;
	prescription: RX_TYPE;
	readerStrength: string | null;
	lensPackage?: LENSES_PACKAGES;
	sellingPlanId: string | null;
	productSubscriptionId: string | null;
};

function getContextFromBundle({
	bundle,
	frameColor,
	baseColor,
	lensColor,
	isV2Subscription = false,
}: BundleContext): BundleReturn {
	const base = bundle?.base;
	const tops = !isV2Subscription
		? bundle?.tops?.filter(top => !top.sellingPlanAllocation)
		: bundle?.tops?.sort((a, b) => {
				if (a.sellingPlanAllocation && !b.sellingPlanAllocation) return -1;
				if (!a.sellingPlanAllocation && b.sellingPlanAllocation) return 1;
				return 0;
			});
	const saved = parseBaseFrameVariant(base?.frame?.variant?.option);
	const subscriptionProduct = bundle?.tops?.find(variant => variant.sellingPlanAllocation !== null);

	return {
		color: getColor(decodeURI(frameColor), baseColor),
		lensColor: getLensColor(decodeURI(lensColor)),
		tops: getTops(base, tops),
		lenses: saved?.lensType,
		prescription: saved?.rxType,
		lensPackage: saved?.lensPackage,
		readerStrength: base?.frame?.properties?.[CART_LINE_ATTRIBUTE_KEYS.READER_STRENGTH] ?? null,
		sellingPlanId: subscriptionProduct?.sellingPlanAllocation?.sellingPlan.id ?? null,
		productSubscriptionId: subscriptionProduct?.variant.id ?? null,
	};
}

function getLensColor(lensColor: string): LENS_COLORS {
	return Object.values(LENS_COLORS).find(color => color === lensColor) || LENS_COLORS.GREEN;
}

function getColor(frameColor: string, baseColor: string): string {
	return Object.values(FRAME_COLORS).find(color => color === frameColor || color === baseColor) || FRAME_COLORS.BLACK;
}

function getTops(base: Bundle['base'], tops: NormalizedCartLine[]) {
	const hasBaseInBundle = !!base;
	return (
		(hasBaseInBundle &&
			tops?.reduce(
				(previous, { variant, properties }) => ({
					...previous,
					[variant.product.handle]: {
						...variant,
						collection: properties[CART_LINE_ATTRIBUTE_KEYS.COLLECTION_PATH] ?? null,
						// NOTE: Must add following 'NormalizedVariant' properties
						handle: variant.product.handle,
						name: variant.name,
					},
				}),
				{}
			)) ??
		{}
	);
}

export { getContextFromBundle };
